/**
 * Application chrome: sidebar, top bar, and the guard that keeps unauthenticated
 * users out.
 *
 * Navigation is filtered by permission, so a bench analyst does not see an
 * Administration section they cannot open, and the labels come from the
 * terminology bundle so a renamed entity renames its menu item too.
 */

import {
  Beaker,
  BellDot,
  Building2,
  ClipboardList,
  FileCheck2,
  FlaskConical,
  Gauge,
  Inbox,
  Languages,
  LayoutDashboard,
  Library,
  LogOut,
  Menu,
  Moon,
  Network,
  MessageSquareWarning,
  MessageSquareText,
  Boxes,
  BookOpenCheck,
  BadgeCheck,
  FileSpreadsheet,
  LayoutTemplate,
  ShieldQuestion,
  PackageCheck,
  PackageSearch,
  PanelLeftClose,
  PanelLeftOpen,
  PlaneTakeoff,
  Plug,
  RotateCcw,
  Search,
  Settings,
  DatabaseBackup,
  ShieldCheck,
  Sun,
  Trash2,
  UserCheck,
  Users,
  Wrench,
  ClipboardCheck,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { navPathFor } from "@/components/layout/Breadcrumbs";
import { useInboxSummary } from "@/hooks/useWorkflow";
import { useRealtimeStatus, useRealtimeStream } from "@/hooks/useRealtime";
import { useCan, useMe } from "@/hooks/useSessionData";
import { useI18n } from "@/i18n/I18nProvider";
// Imported directly rather than lazily: it is the only thing a person with an
// outstanding password change may see, so waiting on a chunk would show them a
// blank screen at exactly the wrong moment.
import ProfilePage from "@/pages/ProfilePage";
import { signOut, useSession } from "@/lib/auth";
import { SessionProblem } from "@/components/layout/SessionProblem";
import { Avatar } from "@/components/ui/Avatar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Toasts } from "@/components/ui/Toasts";
import { cn, toTitleCase } from "@/lib/format";
import { applyAppearance, resolveAppearance, type Appearance } from "@/lib/theme";

interface NavEntry {
  to: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  permissions?: string[];
  badge?: number;
}

interface NavGroup {
  titleKey?: string;
  entries: NavEntry[];
}

// Whether the sidebar is showing as a narrow icon rail. Remembered per browser
// rather than per account: it is a choice about this screen — a laptop on a
// bench wants the width back, the same person at a desk does not — and reading
// it from storage on the first render means the layout does not jump after the
// profile arrives.
const RAIL_KEY = "lims.nav.collapsed";

function resolveCollapsed(): boolean {
  return window.localStorage.getItem(RAIL_KEY) === "true";
}

export function AppShell() {
  const { data: me, isLoading, isError, error, isFetching, refetch } = useMe();
  const { data: session, isPending } = useSession();
  const { data: inbox } = useInboxSummary();
  const { t } = useI18n();
  const can = useCan(me);

  // The live stream. Held open for as long as the shell is mounted, which is for
  // as long as somebody is signed in and working - and not a moment before: an
  // account still on a temporary password may call nothing but the password
  // change, so opening a stream for it would only produce a refused connection
  // retrying in the background.
  const sessionUsable = Boolean(me) && !me?.must_change_password;
  const { notices, dismiss } = useRealtimeStream(sessionUsable);
  const streamStatus = useRealtimeStatus();

  // The one line under somebody's name in the header. Somebody can hold several
  // postings at once — heading a desk while covering its bench is ordinary
  // staffing here — and only one of them fits. Picked by level rather than by
  // position: the list arrives sorted, but a header that quietly depended on the
  // API's ordering would start naming people wrongly the day that changed.
  const postings = [...(me?.roles ?? [])].sort((a, b) => b.level - a.level);
  const seniorRole = postings[0];
  // Hover shows every posting, senior first — and rescues the text from the
  // 10rem truncation, which cuts most of these role names in half.
  const roleSummary = postings.map((entry) => entry.role.name).join(" · ");
  // Samples the desk is holding because a client has not answered yet. Badged in
  // the sidebar because nothing else on screen would show them: a held sample has
  // left the reception queue without arriving anywhere else.
  const { data: confirmations } = useQuery({
    queryKey: ["discrepancies", "count"],
    queryFn: () => api.get<{ total: number }>("/v1/discrepancies", { open_only: true, page_size: 1 }),
    refetchInterval: 120_000,
    enabled: can("sample.view"),
  });

  // Portions an analyst has been assigned and has not been given. Badged for the
  // same reason confirmations are: an analysis waiting on a sample has left the
  // unassigned filter on the worklist and appears on no other screen, so without a
  // number here the ask depends on somebody walking down a corridor.
  // Counted the way the screen is read: a desk that can record a release wants the
  // whole queue, an analyst wants the rows with their own name on them. A bench
  // badged with the centre's total would be a number nobody there can act on.
  const { data: handovers } = useQuery({
    queryKey: ["handovers", "count", can("sample.transfer")],
    queryFn: () =>
      api.get<{ total: number }>("/v1/handovers", {
        mine: !can("sample.transfer"),
        page_size: 1,
      }),
    refetchInterval: 120_000,
    enabled: can("sample.view"),
  });

  // The badge counts what is overdue, not what is open: seven analyses out at
  // laboratories that are all on time is not news, and two that are late is.
  // Retests waiting on somebody. A badge for the same reason the outsourcing one
  // exists: an analysis whose result is disputed is off every worklist, and without
  // a count it waits on whoever remembers to look.
  const { data: retests } = useQuery({
    queryKey: ["retests", "open-count"],
    queryFn: () => api.get<{ total: number }>("/v1/retests", { open_only: true, page_size: 1 }),
    refetchInterval: 120_000,
    enabled: can("test.view"),
  });

  const { data: outsourcings } = useQuery({
    queryKey: ["outsourcings", "overdue-count"],
    queryFn: () => api.get<{ total: number }>("/v1/outsourcings", { overdue: true, page_size: 1 }),
    refetchInterval: 120_000,
    enabled: can("outsourcing.view"),
  });

  // Customer messages nobody has opened. Counted for the same reason the
  // retests are: a complaint that reaches the inbox and waits there is the one
  // the next assessment asks about, and the bell is easy to dismiss.
  const { data: feedback } = useQuery({
    queryKey: ["feedback", "new-count"],
    queryFn: () =>
      api.get<{ total: number }>("/v1/feedback", { status: ["new"], page_size: 1 }),
    refetchInterval: 120_000,
    enabled: can("feedback.view"),
  });
  const newFeedback = feedback?.total ?? 0;

  // What the store needs somebody to do: a delivery waiting on its incoming
  // check, an item at its reorder level, a batch past its date. Counted rather
  // than listed, because the sidebar's job is to say "look here", and because a
  // quarantined lot is invisible everywhere else — it is stock the shelf has and
  // the laboratory cannot use.
  const { data: storeSummary } = useQuery({
    queryKey: ["inventory", "summary"],
    queryFn: () => api.get<{
      lots_in_quarantine: number;
      items_low: number;
      items_out: number;
      lots_expired: number;
    }>("/v1/inventory/summary"),
    refetchInterval: 300_000,
    enabled: can("inventory.view"),
  });
  const storeAttention =
    (storeSummary?.lots_in_quarantine ?? 0) +
    (storeSummary?.items_low ?? 0) +
    (storeSummary?.items_out ?? 0) +
    (storeSummary?.lots_expired ?? 0);

  // Documents in force that this person has not acknowledged. Their own count,
  // not the centre's: the sidebar badge is a task list, and "eleven documents are
  // overdue for review somewhere" is not a task anybody can act on from here.
  const { data: documentSummary } = useQuery({
    queryKey: ["documents", "summary"],
    queryFn: () => api.get<{ my_unread: number }>("/v1/documents/summary"),
    refetchInterval: 300_000,
    enabled: can("document.view"),
  });
  const unreadDocuments = documentSummary?.my_unread ?? 0;

  // Requests somebody has asked to have deleted. These are also tasks in My
  // tasks for whoever may answer them, so the badge here is for the desk that
  // asked as much as for the person answering: a frozen request does not leave
  // the request list and does not change status, so without a count nothing on
  // screen says an answer is still outstanding.
  const { data: deletions } = useQuery({
    queryKey: ["request-deletions", "count"],
    queryFn: () =>
      api.get<{ total: number }>("/v1/request-deletions", {
        status: "pending",
        page_size: 1,
      }),
    refetchInterval: 120_000,
    enabled: can("request.confirm_deletion", "request.request_deletion"),
  });

  // Disposal work waiting on somebody, counted the way each side reads the screen:
  // quality wants the runs put to it; the desk wants what is past its date and the
  // runs it may now carry out.
  const { data: disposalSummary } = useQuery({
    queryKey: ["sample-disposals", "summary"],
    queryFn: () =>
      api.get<{ overdue: number; awaiting_approval: number; ready_to_carry_out: number }>(
        "/v1/sample-disposals/register/summary",
      ),
    refetchInterval: 300_000,
    enabled: can("sample.dispose", "sample.approve_disposal"),
  });
  const disposalAttention = can("sample.approve_disposal")
    ? (disposalSummary?.awaiting_approval ?? 0)
    : (disposalSummary?.overdue ?? 0) + (disposalSummary?.ready_to_carry_out ?? 0);

  // Delegations of authority waiting on a decision. Read for the same reason as
  // the deletions count above: a pending delegation changes nothing anywhere
  // else on screen, so without a number here the Director has only the
  // notification, and the person who asked has nothing at all.
  //
  // Asked for by everybody, because anybody can now ask for cover. The endpoint
  // narrows the register to the rows the caller is party to, so for most people
  // this counts their own outstanding ask and is a bare 0 the rest of the time.
  const { data: delegations } = useQuery({
    queryKey: ["delegations", "count"],
    queryFn: () =>
      api.get<{ total: number }>("/v1/admin/delegations", {
        status: "pending",
        active_only: false,
        page_size: 1,
      }),
    refetchInterval: 120_000,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(resolveCollapsed);
  const [appearance, setAppearance] = useState<Appearance>(resolveAppearance);
  // Below the large breakpoint the sidebar is a drawer that is either on screen
  // or off it, and a 4rem drawer would be a menu nobody can read. So the rail is
  // what the collapsed state looks like on a wide screen; the drawer, whenever it
  // is open, is always the full list.
  const railed = collapsed && !mobileOpen;
  // Signing out is one click away from work that is not finished: an analyst with a
  // half-filled worksheet, a reviewer part-way through a batch. Asked once here and
  // shared by every sign-out button in the chrome, so what the button does is the
  // same answer wherever it appears.
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const signOutDialog = (
    <ConfirmDialog
      open={confirmingSignOut}
      title="Sign out?"
      description="You will be returned to the sign-in screen. Anything you have typed but not saved is lost."
      confirmLabel="Sign out"
      cancelLabel="Stay signed in"
      icon={<LogOut size={16} />}
      busy={signingOut}
      onCancel={() => setConfirmingSignOut(false)}
      onConfirm={() => {
        setSigningOut(true);
        // signOut navigates away on success, so there is nothing to do after it.
        // On failure the button has to come back, or the dialog spins for ever
        // over a session the person is still in.
        void signOut().catch(() => setSigningOut(false));
      }}
    />
  );

  useEffect(() => {
    applyAppearance(appearance);
  }, [appearance]);

  useEffect(() => {
    window.localStorage.setItem(RAIL_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Command palette: Ctrl/Cmd-K jumps to search, which is how staff who live in
  // this product all day actually navigate.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        document.getElementById("global-search")?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!isPending && !session?.user) {
      // Carry the address along. Somebody who scanned a sample label on a phone
      // that was not signed in wants that sample afterwards, not the dashboard.
      const here = `${location.pathname}${location.search}${location.hash}`;
      navigate(here === "/" ? "/sign-in" : `/sign-in?next=${encodeURIComponent(here)}`, {
        replace: true,
      });
    }
  }, [isPending, session, navigate]);

  const groups = useMemo<NavGroup[]>(
    () => [
      {
        entries: [
          { to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard },
          {
            to: "/inbox",
            labelKey: "nav.inbox",
            icon: Inbox,
            badge: inbox?.total,
          },
        ],
      },
      {
        titleKey: "nav.requests",
        entries: [
          {
            to: "/requests",
            labelKey: "nav.requests",
            icon: ClipboardList,
            permissions: ["request.view"],
          },
          {
            to: "/reception",
            labelKey: "nav.reception",
            icon: PackageSearch,
            permissions: ["sample.receive", "sample.view"],
          },
          {
            to: "/reception/confirmations",
            labelKey: "nav.confirmations",
            icon: MessageSquareWarning,
            permissions: ["sample.view"],
            badge: confirmations?.total || undefined,
          },
          {
            to: "/reception/handovers",
            labelKey: "nav.handovers",
            icon: PackageCheck,
            permissions: ["sample.view"],
            badge: handovers?.total || undefined,
          },
          {
            to: "/reception/disposal",
            labelKey: "nav.disposal",
            icon: Trash2,
            // Either half: the desk that draws runs up, and quality who approves them.
            permissions: ["sample.dispose", "sample.approve_disposal"],
            badge: disposalAttention || undefined,
          },
          {
            to: "/requests/deletions",
            labelKey: "nav.deletions",
            icon: ShieldQuestion,
            // Either half of the pair: the desk that asked wants to see whether it
            // has been answered, and whoever answers needs the register.
            permissions: ["request.confirm_deletion", "request.request_deletion"],
            badge: deletions?.total || undefined,
          },
          {
            to: "/certificates",
            labelKey: "nav.certificates",
            icon: FileCheck2,
            permissions: ["certificate.view"],
          },
        ],
      },
      {
        titleKey: "nav.worklist",
        entries: [
          {
            to: "/worklist",
            labelKey: "nav.worklist",
            icon: FlaskConical,
            permissions: ["test.view"],
          },
          {
            // The sheets an analyst fills for several samples at once. Beside the
            // worklist that lists the samples one by one, behind the right to
            // enter results, because a sheet is written by the bench and nobody
            // else opens one.
            to: "/worksheet-runs",
            labelKey: "nav.worksheet_runs",
            icon: FileSpreadsheet,
            permissions: ["test.enter_result"],
          },
          {
            // The documents the methods on this person's bench cite, beside the
            // worklist that puts those methods there. Behind `test.execute` rather
            // than `test.view`: reception and client service can view analyses but
            // never have one on their bench, so the entry would only ever open empty.
            to: "/bench-binder",
            labelKey: "nav.bench_binder",
            icon: Library,
            permissions: ["test.execute"],
          },
          {
            // Analyses with another laboratory. On the worklist group because that is
            // whose work it is, even while the sample is in somebody else's building.
            //
            // "Outsourced analyses", not "External laboratories" - this is the work, and
            // the register of who may receive it is a different screen under the
            // catalogue. Both read "External laboratories" until 2026-07-30, which put
            // the same words twice in one sidebar and told you nothing about either.
            to: "/outsourcing",
            labelKey: "nav.outsourcing",
            icon: PlaneTakeoff,
            permissions: ["outsourcing.view"],
            badge: outsourcings?.total || undefined,
          },
          {
            // Work this centre has had to do twice. On the worklist group because
            // that is whose work it is, and its own entry rather than a tab on the
            // worklist because the people who read it - quality, the desk heads
            // deciding one - mostly are not looking at a worklist when they do.
            to: "/retests",
            labelKey: "nav.retests",
            icon: RotateCcw,
            permissions: ["test.view"],
            badge: retests?.total || undefined,
          },
          {
            to: "/equipment",
            labelKey: "nav.equipment",
            icon: Wrench,
            permissions: ["equipment.view"],
          },
          {
            // No permission asked: a log is filled by whoever is named on it, and the
            // Desk Head may name anyone. The page says there is nothing waiting.
            to: "/monitoring",
            labelKey: "nav.monitoring",
            icon: ClipboardCheck,
          },
          {
            // Next to equipment because clause 6.4 covers instruments and
            // materials in one breath, and because the two are the same job: an
            // analysis is only as traceable as the balance it was weighed on and
            // the batch of medium it was grown in. Badged with what needs doing —
            // a delivery nobody has checked in blocks work while looking like
            // stock.
            to: "/inventory",
            labelKey: "nav.inventory",
            icon: Boxes,
            permissions: ["inventory.view"],
            badge: storeAttention || undefined,
          },
          {
            to: "/instrument-data",
            labelKey: "nav.instruments",
            icon: Gauge,
            permissions: ["equipment.ingest"],
          },
          {
            to: "/instruments/connections",
            labelKey: "nav.connections",
            icon: Plug,
            permissions: ["equipment.view"],
          },
        ],
      },
      {
        // The Authority's own section. One entry today; a section rather than a
        // stray item because what belongs here - oversight of the centre, by
        // somebody who is not part of it - is not any of the groups below.
        titleKey: "nav.authority",
        entries: [
          {
            to: "/oversight",
            labelKey: "nav.oversight",
            icon: Gauge,
            permissions: ["report.oversight"],
          },
        ],
      },
      {
        titleKey: "nav.catalog",
        entries: [
          {
            to: "/catalog",
            labelKey: "nav.catalog",
            icon: Beaker,
            permissions: ["catalog.view"],
          },
          {
            to: "/catalog/external-labs",
            labelKey: "nav.external_labs",
            icon: Building2,
            permissions: ["catalog.view"],
          },
          {
            // The quality system's own paperwork. In the catalogue group because
            // that is where a laboratory keeps its reference material - the
            // methods, the standards behind them, and now the procedures that say
            // how they are run. Badged with what this person has not read: an
            // unacknowledged revision is the one thing here that is somebody's
            // outstanding task rather than a register to consult.
            to: "/documents",
            labelKey: "nav.documents",
            icon: BookOpenCheck,
            permissions: ["document.view"],
            badge: unreadDocuments || undefined,
          },
          {
            // The worksheet each method is recorded on, per method and per sample
            // type, designed here and signed like any controlled form. Its own
            // entry rather than a tab on the register, because the question it
            // answers is "which methods have no worksheet yet", and a register of
            // documents cannot show an absence.
            to: "/documents/worksheets",
            labelKey: "nav.worksheet_templates",
            icon: LayoutTemplate,
            permissions: ["document.manage"],
          },
          {
            // What the centre can show its methods actually do. Next to the
            // catalogue that lists them, because the two answer halves of one
            // question: which determinations are offered, and on what evidence.
            to: "/validations",
            labelKey: "nav.validation",
            icon: BadgeCheck,
            permissions: ["validation.view"],
          },
          {
            to: "/clients",
            labelKey: "nav.clients",
            icon: Building2,
            permissions: ["client.view"],
          },
          {
            // What customers said about the centre, written in through a page
            // that asks for no account. Next to the clients because it is the
            // other half of "who we work for": the register says who they are,
            // this says how it went. Badged with what nobody has opened yet.
            to: "/feedback",
            labelKey: "nav.feedback",
            icon: MessageSquareText,
            permissions: ["feedback.view"],
            badge: newFeedback || undefined,
          },
          {
            // The lists every form picks from: companies, countries, units, stores
            // and the controlled phrases. In the catalogue group because that is
            // what they are - reference data the laboratory keeps - and next to
            // the clients because the company register is the other half of "who
            // are we dealing with".
            to: "/admin/registers",
            labelKey: "nav.registers",
            icon: Library,
            permissions: ["catalog.view"],
          },
          // No permission: the organogram is reference data every screen names, and an
          // analyst who cannot read it meets blanks where their own lab should be. The
          // screen itself is read-only without `admin.departments`.
          {
            to: "/admin/departments",
            labelKey: "nav.departments",
            icon: Network,
          },
        ],
      },
      {
        titleKey: "nav.admin",
        entries: [
          // No permission, and the only entry in this section without one. Arranging
          // cover for your own leave is not administration, but it is the same kind
          // of errand as the rest of this section - something you do about your
          // account rather than about a sample - and a person looking for it has one
          // place to look. The Director's queue and an analyst's single ask are the
          // same screen, so the badge is the count each of them is owed.
          {
            to: "/delegations",
            labelKey: "nav.delegations",
            icon: UserCheck,
            badge: delegations?.total || undefined,
          },
          // `can(...)` is an OR, so listing `workflow.view` here put the designer in
          // the sidebar of nearly every role — twelve of fourteen hold it. Designing
          // an approval chain is the System Administrator's task, and this is the
          // administration section; the page itself still renders read-only for a
          // `workflow.view` holder who arrives by address, so nothing is hidden that
          // someone needs to read.
          {
            to: "/admin/workflows",
            labelKey: "nav.workflows",
            icon: ShieldCheck,
            permissions: ["workflow.manage"],
          },
          {
            to: "/admin/users",
            labelKey: "nav.users",
            icon: Users,
            permissions: ["admin.users"],
          },
          {
            to: "/admin/labels",
            labelKey: "nav.labels",
            icon: Languages,
            permissions: ["admin.labels"],
          },
          {
            to: "/admin/settings",
            labelKey: "nav.settings",
            icon: Settings,
            permissions: ["admin.settings"],
          },
          {
            to: "/admin/backups",
            labelKey: "nav.backups",
            icon: DatabaseBackup,
            permissions: ["admin.backup"],
          },
        ],
      },
    ],
    // Every badge the list reads. `inbox?.total` was the only entry until the
    // store was added, which meant a confirmation or an overdue outsourcing
    // arriving on its own never reached the sidebar: the memo had no reason to
    // recompute and kept showing the count it was built with.
    [
      inbox?.total,
      confirmations?.total,
      handovers?.total,
      outsourcings?.total,
      deletions?.total,
      disposalAttention,
      delegations?.total,
      storeAttention,
      unreadDocuments,
      newFeedback,
    ],
  );

  // Which single entry is lit.
  //
  // Prefix matching alone cannot answer this: /analyses/:id belongs to the
  // worklist without sharing a prefix with it, and /catalog/methods/new belongs
  // to the catalogue even though /catalog/external-labs is a sibling entry that
  // also starts with /catalog. The route registry behind the breadcrumbs already
  // knows every screen's ancestry, so the sidebar asks it — one source of truth
  // for "where am I", shared by the trail at the top of a detail page and the
  // highlight on the left.
  const activeNav = useMemo(() => {
    const paths = new Set(groups.flatMap((group) => group.entries.map((entry) => entry.to)));
    return navPathFor(location.pathname, paths);
  }, [groups, location.pathname]);

  if (isLoading || isPending) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="size-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          Loading the laboratory…
        </div>
      </div>
    );
  }

  if (isError || !me) {
    return (
      <>
        {signOutDialog}
        <SessionProblem
          error={error}
          retrying={isFetching}
          onRetry={() => void refetch()}
          onSignOut={() => setConfirmingSignOut(true)}
          email={session?.user?.email}
        />
      </>
    );
  }

  // A password somebody else chose is not a session anyone should work in. The API
  // refuses every call but the change itself, so rendering the application would
  // only produce a screen full of errors; this is the same rule, said once.
  if (me.must_change_password) {
    return (
      <div className="grid min-h-screen place-items-center bg-background p-6">
        {signOutDialog}
        <div className="w-full max-w-md">
          <div className="mb-5 flex items-center gap-2.5">
            <span className="grid size-9 place-items-center rounded-lg bg-brand">
              <ShieldCheck size={18} className="text-white" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold">
                {me.organization.center_code ?? "LIMS"}
              </span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {me.user.full_name} · {me.user.email}
              </span>
            </span>
            <button
              className="btn btn-ghost ml-auto h-8 px-2 text-xs"
              onClick={() => setConfirmingSignOut(true)}
            >
              <LogOut size={14} /> {toTitleCase("Sign out")}
            </button>
          </div>
          <ProfilePage forced />
        </div>
      </div>
    );
  }


  const centre = me.organization.center_name;

  return (
    <div
      className="min-h-screen bg-background"
      // One variable drives both the rail's width and the gap the page leaves for
      // it, so the two can never disagree. Only read from the large breakpoint up;
      // below it the sidebar is a drawer at its full width.
      style={
        {
          "--nav-width": collapsed
            ? "var(--sidebar-width-collapsed)"
            : "var(--sidebar-width)",
        } as CSSProperties
      }
    >
      {/* ---------------------------------------------------------------- nav */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground transition-[transform,width] lg:w-(--nav-width) lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
        data-print="hide"
      >
        <div
          className={cn(
            "flex h-topbar items-center gap-2.5 border-b border-sidebar-border",
            railed ? "justify-center px-2" : "px-4",
          )}
        >
          <span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-lg bg-white">
            <img
              src="/logo.jpg"
              alt=""
              className="size-8 object-contain"
              onError={(event) => {
                // No tenant logo installed: fall back to the generic mark.
                event.currentTarget.replaceWith(
                  Object.assign(document.createElement("span"), {
                    className: "text-gold text-sm font-bold",
                    textContent: "L",
                  }),
                );
              }}
            />
          </span>
          {railed ? null : (
            <>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-white">
                  {me.organization.center_code ?? "LIMS"}
                </span>
                <span className="block truncate text-[11px] text-sidebar-muted">
                  {me.organization.code}
                </span>
              </span>
              <button
                className="ml-auto text-sidebar-muted lg:hidden"
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation"
              >
                <X size={18} />
              </button>
            </>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {groups.map((group, index) => {
            const visible = group.entries.filter(
              (entry) => !entry.permissions || can(...entry.permissions),
            );
            if (visible.length === 0) return null;
            return (
              <div key={group.titleKey ?? index} className="mb-1">
                {group.titleKey ? (
                  // Nothing on a rail is wide enough for "ADMINISTRATION", so the
                  // heading becomes the one thing it was doing anyway: a line
                  // saying the entries below it are a different kind of work.
                  railed ? (
                    <span className="mx-auto my-2 block h-px w-6 bg-sidebar-border" />
                  ) : (
                    <p className="nav-section">{t(group.titleKey)}</p>
                  )
                ) : null}
                {visible.map((entry) => (
                  // aria-current="page" is what index.css styles. It is set from
                  // the route registry rather than from NavLink's own prefix
                  // matching, so a detail page lights the entry it belongs to —
                  // a worksheet lights "Worklist", a method form the catalogue.
                  <Link
                    key={entry.to}
                    to={entry.to}
                    aria-current={entry.to === activeNav ? "page" : undefined}
                    className={cn("nav-item", railed && "relative justify-center px-0")}
                    // On the rail the icon is all there is, so the label has to
                    // reach a screen reader some other way, and a pointer has to
                    // be able to ask what it is.
                    title={railed ? t(entry.labelKey) : undefined}
                    aria-label={railed ? t(entry.labelKey) : undefined}
                  >
                    <entry.icon size={16} className="shrink-0" />
                    {railed ? null : <span className="truncate">{t(entry.labelKey)}</span>}
                    {entry.badge ? (
                      railed ? (
                        // Over the icon rather than beside it: there is no room
                        // beside it, and a count nobody can see is a count that
                        // does not do its job.
                        <span className="absolute right-1.5 top-0.5 min-w-4 rounded-full bg-gold px-1 text-center text-[9px] font-bold leading-4 text-[#3b2600]">
                          {entry.badge}
                        </span>
                      ) : (
                        <span className="ml-auto rounded-full bg-gold px-1.5 text-[10px] font-bold text-[#3b2600]">
                          {entry.badge}
                        </span>
                      )
                    ) : null}
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>

        {/* The centre and its accreditation. Dropped on the rail rather than
            truncated to three characters, which would name nothing. */}
        {railed ? null : (
          <div className="border-t border-sidebar-border px-3 py-3 text-[11px] text-sidebar-muted">
            <p className="truncate">{centre}</p>
            {me.organization.accreditation_number ? (
              <p className="mt-0.5 truncate">
                {me.organization.accreditation_body} {me.organization.accreditation_number} ·{" "}
                {me.organization.accreditation_standard}
              </p>
            ) : null}
          </div>
        )}
      </aside>

      {mobileOpen ? (
        <div
          className="fixed inset-0 z-30 bg-foreground/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          role="presentation"
        />
      ) : null}

      {/* -------------------------------------------------------------- main */}
      <div className="transition-[padding] lg:pl-(--nav-width)">
        <header
          className="sticky top-0 z-20 flex h-topbar items-center gap-3 border-b border-border bg-topbar px-4"
          data-print="hide"
        >
          <button
            className="btn btn-ghost h-9 px-2 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={18} />
          </button>

          {/* The same control on a wide screen, where the sidebar is not a drawer
              to open and close but a column to widen and narrow. */}
          <button
            className="btn btn-ghost hidden h-9 px-2 lg:inline-flex"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-pressed={collapsed}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>

          <form
            className="relative max-w-md flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              const value = new FormData(event.currentTarget).get("q");
              if (value) navigate(`/search?q=${encodeURIComponent(String(value))}`);
            }}
          >
            <Search
              size={15}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="global-search"
              name="q"
              className="field pl-8 pr-14"
              placeholder="Search samples, requests, certificates…"
              autoComplete="off"
            />
            <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
              ⌘K
            </kbd>
          </form>

          <div className="ml-auto flex items-center gap-1">
            <button
              className="btn btn-ghost h-9 px-2"
              onClick={() =>
                setAppearance(appearance === "dark" ? "light" : "dark")
              }
              aria-label="Toggle dark mode"
            >
              {appearance === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <Link
              to="/inbox"
              className="btn btn-ghost relative h-9 px-2"
              aria-label={t("nav.inbox")}
            >
              <BellDot size={16} />
              {inbox?.overdue ? (
                <span className="absolute right-1 top-1 size-2 rounded-full bg-status-overdue" />
              ) : null}
            </Link>

            {/* Whether this screen is being told about new work or is waiting to
                notice it. Shown only when it is not, and worded plainly: somebody
                who has stopped seeing tasks appear needs to know the difference
                between a quiet morning and a dead connection. */}
            {streamStatus !== "live" ? (
              <span
                className="hidden items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground sm:inline-flex"
                title={
                  streamStatus === "connecting"
                    ? "Reconnecting to the live update stream."
                    : "Not receiving live updates. Lists refresh on their own timer instead."
                }
              >
                <span className="size-1.5 rounded-full bg-status-pending" />
                {streamStatus === "connecting" ? "Reconnecting" : "Offline"}
              </span>
            ) : null}

            <div className="ml-1 flex items-center gap-2 border-l border-border pl-2">
              {/* The whole identity block is the way to your own account - the
                  place people look for it, and the only route to changing your
                  own password. */}
              <Link
                to="/profile"
                className="flex min-w-0 items-center gap-2 rounded-md px-1 py-1 hover:bg-muted"
                title="Your account"
              >
                <Avatar
                  name={me.user.full_name}
                  src={me.user.avatar_path ? "/v1/me/avatar" : null}
                  version={me.user.avatar_path}
                  className="size-8 text-xs"
                />
                <span className="hidden min-w-0 sm:block">
                  <span className="block max-w-[10rem] truncate text-xs font-semibold">
                    {me.user.full_name}
                  </span>
                  {/* One line, and for somebody with several postings it has to be
                      the senior one. A desk head who also covers the bench is two
                      postings, and reading "Analyst" under their own name in the
                      header is not wrong so much as arbitrary — it was whichever
                      row came back first. Chosen by level rather than by position,
                      so this does not quietly depend on the API's ordering. */}
                  <span
                    className="block max-w-[10rem] truncate text-[11px] text-muted-foreground"
                    title={roleSummary || undefined}
                  >
                    {seniorRole?.role.name ?? me.user.job_title ?? ""}
                  </span>
                </span>
              </Link>
              <button
                className="btn btn-ghost h-9 px-2"
                onClick={() => setConfirmingSignOut(true)}
                aria-label="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-(--content-max) px-4 pb-24 pt-6 lg:px-6">
          <Outlet />
        </main>
      </div>

      <Toasts notices={notices} onDismiss={dismiss} />
      {signOutDialog}
    </div>
  );
}
